import React, { useMemo, useState } from 'react';
import { sumBy } from 'lodash';
import { capitalize } from 'lodash';

import Wizard from 'Views/Components/Wizard/Wizard';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import Steps from 'Views/Components/Steps/Steps';
import Spinner from 'Views/Components/Spinner/Spinner';
import CompletedCourseAccordion from 'Views/Components/CompletedCourseAccordion/CompletedCourseAccordion';

import { IStudyPlanWizardData } from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import StudyPlanWizardHeader from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardHeader';
import StudyPlanWizardSidebar from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizardSidebar';
import StudyPlanYear from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlan/StudyPlanYear';
import { useRequirementIndex }
	from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import PlanNavigationFooter from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlan/StudyPlanFooter';

import useStudyPlan from 'Queries/Hooks/StudyPlan/useStudyPlan';
import useScreenSize from 'Hooks/useScreenSize';
import { getSelectedCourseLocations } from 'Util/CourseUtils';
import StudyPlanPdf from 'Views/Pages/Pdf/StudyPlanPdf';
import {
	useCourseDuplicates,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/useCourseDuplicates';
import usePdfExport from 'Hooks/usePdfExport';

const StudyPlan = () => {
	const {
		data,
		step,
		stepLabels,
		goToStep,
	} = useWizardContext<IStudyPlanWizardData>();
	const planDescription = data?.studyPlan?.programVersion?.studyPlanDescription;

	const {
		planSelections,
		coursesById,
		duplicateCourseIds,
		requirementTree,
		requirementsById,
		completedCourseIds,
		setCurrentComponentId,
	} = useRequirementIndex();

	const duplicateCourses = useCourseDuplicates(
		planSelections,
		duplicateCourseIds,
		requirementsById,
		coursesById,
	);

	const {
		currentPlan,
		courses,
		allTerms,
		allStudyPeriods,
		completedCoursesById,
		resetPlanStudyLoad,
		togglePeriodStudyLoad,
		adjustTermStudyLoad,
		requisiteWarningsByCourseId,
		isStudyLoadSetToDefaultValues,
		getProgramTermConfig,
		isInitialDataLoading,
		termsGroupedByYear,
		getCurrentPeriodStudyLoad,
		getCurrentTermStudyLoad,
		startingYear,
	} = useStudyPlan(data?.studyPlan?.id);
	const isMobile = useScreenSize();
	const [selectedYear, setSelectedYear] = useState(0);

	const planAssignedUserName = data?.studyPlan?.getAssignedUser().getDisplayName();

	const currentSelectedPlanRequirementIds = useMemo(
		() => data?.studyPlan?.getSelectedRequirements(),
		[data?.studyPlan],
	);

	const getComponentNames = (courseId: string) => {
		if (
			!data?.studyPlan
			|| !currentSelectedPlanRequirementIds
			|| !requirementsById
		) {
			return [];
		}

		return getSelectedCourseLocations(courseId, data.studyPlan.planSelections, requirementsById)
			.map(component => component.displayName);
	};

	const getCourseUnitTotal = (courseIds: string[]) => {
		if (!courses) {
			return 0;
		}

		return sumBy(
			courseIds,
			/**
			 * course may be present in scheduled or completed courses
			 * so searching BOTH for the unit value for a given course
			 * is required
			 */
			courseId => (courses[courseId]?.units ?? completedCoursesById[courseId]?.units ?? 0),
		);
	};

	const totalUnitsSelected = useMemo(() => getCourseUnitTotal(
		[
			...Object.keys(completedCoursesById),
			...Object.keys(courses),
		],
	), [completedCoursesById, courses, getCourseUnitTotal]);

	const planContainsCompletedCourses = Object.values(completedCoursesById).length > 0;

	const { isGenerating, generatePdf } = usePdfExport({
		filename: `Study Plan ${planAssignedUserName || ''}.pdf`,
		getPdfElement: () => {
			if (!data?.studyPlan || !allStudyPeriods || !currentPlan || !allTerms) {
				return null;
			}

			return (
				<StudyPlanPdf
					studyPlan={data.studyPlan}
					requirementTree={requirementTree}
					coursesById={coursesById}
					duplicateCourses={duplicateCourses}
					completedCourseIds={completedCourseIds}
					getCourseComponents={getComponentNames}
					allStudyPeriods={allStudyPeriods}
					termsGroupedByYear={termsGroupedByYear}
					currentPlan={currentPlan}
					allTerms={allTerms}
					requisiteWarningsByCourseId={requisiteWarningsByCourseId}
					totalUnitsSelected={totalUnitsSelected}
				/>
			);
		},
	});

	return (
		<>
			<StudyPlanWizardHeader />
			<Wizard.Body>
				<StudyPlanWizardSidebar
					showProgramDetails
					studyPlan={data?.studyPlan}
					onComponentClick={componentId => {
						setCurrentComponentId(componentId);
						goToStep(2);
					}}
				/>
				<Steps
					currentStep={step}
					steps={stepLabels}
					onStepClick={goToStep}
				/>
				<Wizard.Main>
					<div className="content study-plan-page">
						{
							isInitialDataLoading || !currentPlan || !allTerms || !allStudyPeriods
								? <Spinner />
								: (
									<div className="study-plan-detail">
										<h3 className="study-plan-detail__header">
											{capitalize(window.lexicon.studyPlan)}
										</h3>
										<p className="study-plan-detail__description">{planDescription}</p>
										{
											planContainsCompletedCourses
											&& (
												<CompletedCourseAccordion
													courses={Object.values(completedCoursesById)}
													totalUnits={getCourseUnitTotal(
														[
															...Object.keys(completedCoursesById),
															...Object.keys(courses),
														],
													)}
													getCourseComponents={getComponentNames}
												/>
											)
										}
										{
											Object.keys(termsGroupedByYear)
												.map((year, yearIndex) => {
													const generatedTermsForYear = termsGroupedByYear[year];
													const currentYear = generatedTermsForYear[0].year;
													const isFirstYear = yearIndex === 0;

													const isMobileViewingThisYear = (
														isMobile && (selectedYear === yearIndex)
													);

													if (!isMobile || isMobileViewingThisYear) {
														return (
															<StudyPlanYear
																key={currentYear}
																allTerms={allTerms}
																allStudyPeriods={allStudyPeriods}
																generatedPlan={currentPlan}
																// eslint-disable-next-line max-len
																requisiteWarningsByCourseId={requisiteWarningsByCourseId}
																currentYear={currentYear}
																yearIndex={yearIndex}
																generatedTerms={generatedTermsForYear}
																getCourseUnitTotal={getCourseUnitTotal}
																adjustTermStudyLoad={adjustTermStudyLoad}
																resetPlanStudyLoad={() => (
																	currentPlan.studyPlan.id
																	&& resetPlanStudyLoad()
																)}
																togglePeriodStudyLoad={togglePeriodStudyLoad}
																showResetButton={isFirstYear}
																getCourseComponents={getComponentNames}
																disableResetButton={isStudyLoadSetToDefaultValues}
																getProgramTermConfig={getProgramTermConfig}
																getCurrentPeriodStudyLoad={getCurrentPeriodStudyLoad}
																getCurrentTermStudyLoad={getCurrentTermStudyLoad}
															/>
														);
													}

													return null;
												})
										}
									</div>
								)
						}
					</div>
					{
						startingYear !== undefined
							? (
								<PlanNavigationFooter
									startingYear={startingYear}
									isMobile={isMobile}
									selectedYear={selectedYear}
									currentlyViewingFinalYear={
										selectedYear === Object.keys(termsGroupedByYear).length - 1
									}
									setSelectedYear={setSelectedYear}
									isGeneratingPdf={isGenerating}
									exportToPdf={generatePdf}
								/>
							) : null
					}
				</Wizard.Main>
			</Wizard.Body>
		</>
	);
};

export default StudyPlan;
